import { useCallback, useState } from "react";
import { api } from "@/services/api";
import type { ExtractedMetadata, ExtractMetadataRequest } from "@/hooks/useExtractMetadata";
import { mergeRecord, buildTimedOutStatuses } from "@/lib/polling";
import { usePollerRegistry } from "@/hooks/usePollerRegistry";

export type MetadataJobStatus = "Queued" | "Processing" | "Done" | "Error";

export interface MetadataJobInfo {
  status: MetadataJobStatus;
  queue_position?: number;
  result?: ExtractedMetadata;
  error?: string;
}

export interface FileDocumentEntry {
  filename: string;
  document_id: string;
}

interface EnqueueJobInput {
  document_id: string;
  filename: string;
  original_name?: string;
  download_url?: string;
  model?: string;
}

interface EnqueueResponse {
  enqueued: { document_id: string; filename: string }[];
  queue_size: number;
}

interface StatusResponse {
  statuses: Record<string, MetadataJobInfo>;
  all_done: boolean;
  queue_size: number;
}

interface PendingJobsResponse {
  jobs: Array<{
    document_id: string;
    filename: string;
    original_name: string | null;
    download_url: string | null;
    status: MetadataJobStatus;
    queue_position?: number;
  }>;
  queue_size: number;
}

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 120; // 10 minutes

export function useMetadataQueue() {
  // Status map keyed by document_id — mirrors PICO's documentStatuses pattern
  const [documentStatuses, setDocumentStatuses] = useState<Record<string, MetadataJobInfo>>({});
  // Maps file array index → { filename, document_id }
  const [fileDocumentMap, setFileDocumentMap] = useState<FileDocumentEntry[]>([]);
  const [queueSize, setQueueSize] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const { run, stop, stopAll } = usePollerRegistry();

  const stopPolling = useCallback(() => {
    stop("batch");
    setIsPolling(false);
  }, [stop]);

  const startPolling = useCallback(
    (docIds: string[], onAllDone?: (statuses: Record<string, MetadataJobInfo>) => void) => {
      setIsPolling(true);
      run<StatusResponse>("batch", {
        fetchStatus: () => {
          const params = docIds.join(",");
          return api.get<StatusResponse>(
            `/api/metadata-extraction/status?document_ids=${encodeURIComponent(params)}`,
          );
        },
        isDone: (data) => data.all_done,
        onUpdate: (data) => {
          setDocumentStatuses((prev) => mergeRecord(prev, data.statuses));
          setQueueSize(data.queue_size);
        },
        onDone: (data) => {
          setIsPolling(false);
          onAllDone?.(data.statuses);
        },
        onTimeout: (lastData) => {
          // Mark any still-pending tracked doc as a timed-out error; leave
          // anything that already reached a terminal status alone.
          const timedOutStatuses = buildTimedOutStatuses<MetadataJobInfo>(
            docIds,
            lastData,
            (status) => status === "Queued" || status === "Processing",
            { status: "Error", error: "Metadata extraction timed out" },
          );
          setDocumentStatuses((prev) => mergeRecord(prev, timedOutStatuses));
          setIsPolling(false);
          onAllDone?.(timedOutStatuses);
        },
        onError: (err) => console.error("Metadata queue poll error:", err),
        intervalMs: POLL_INTERVAL_MS,
        maxAttempts: MAX_POLLS,
      });
    },
    [run],
  );

  const enqueue = useCallback(
    async (
      requests: ExtractMetadataRequest[],
      projectId?: string,
    ): Promise<FileDocumentEntry[]> => {
      const jobs: EnqueueJobInput[] = requests.map((req) => ({
        document_id: req.document_id!,
        filename: req.filename!,
        original_name: req.original_name,
        download_url: req.download_url,
        model: req.model,
      }));

      const response = await api.post<EnqueueResponse>("/api/metadata-extraction/enqueue", {
        project_id: projectId,
        jobs,
      });

      const mapping: FileDocumentEntry[] = response.enqueued.map((e) => ({
        filename: e.filename,
        document_id: e.document_id,
      }));

      setFileDocumentMap((prev) => [...prev, ...mapping]);
      setQueueSize(response.queue_size);

      // Initialise all as Queued immediately (merge into existing statuses)
      setDocumentStatuses((prev) => {
        const next = { ...prev };
        mapping.forEach((m) => {
          next[m.document_id] = { status: "Queued" };
        });
        return next;
      });

      return mapping;
    },
    [],
  );

  /**
   * Re-enqueue a single document for extraction without replacing fileDocumentMap
   * or interrupting the current polling session. Runs its own independent poller,
   * keyed by document_id, so stopping the batch poll can never affect it.
   */
  const retryDocument = useCallback(
    async (
      request: ExtractMetadataRequest,
      projectId?: string,
      onDone?: (info: MetadataJobInfo) => void,
    ) => {
      const docId = request.document_id!;
      const job: EnqueueJobInput = {
        document_id: docId,
        filename: request.filename!,
        original_name: request.original_name,
        download_url: request.download_url,
        model: request.model,
      };

      const response = await api.post<EnqueueResponse>("/api/metadata-extraction/enqueue", {
        project_id: projectId,
        jobs: [job],
      });
      setQueueSize(response.queue_size);

      // Update just this document's status to Queued without replacing the full map
      setDocumentStatuses((prev) => mergeRecord(prev, { [docId]: { status: "Queued" } }));

      run<StatusResponse>(`retry:${docId}`, {
        fetchStatus: () =>
          api.get<StatusResponse>(
            `/api/metadata-extraction/status?document_ids=${encodeURIComponent(docId)}`,
          ),
        isDone: (data) => {
          const status = data.statuses[docId]?.status;
          return status === "Done" || status === "Error";
        },
        onUpdate: (data) => {
          setDocumentStatuses((prev) => mergeRecord(prev, data.statuses));
        },
        onDone: (data) => {
          onDone?.(data.statuses[docId]);
        },
        onTimeout: () => {
          const timedOut: MetadataJobInfo = { status: "Error", error: "Metadata extraction timed out" };
          setDocumentStatuses((prev) => mergeRecord(prev, { [docId]: timedOut }));
          onDone?.(timedOut);
        },
        onError: (err) => console.error("Retry poll error:", err),
        intervalMs: POLL_INTERVAL_MS,
        maxAttempts: MAX_POLLS,
      });
    },
    [run],
  );

  /**
   * Set or update a single entry in fileDocumentMap at a specific index.
   * Used when retrying a document that doesn't have an existing map entry
   * (e.g. initial enqueue failed), so the useEffect that syncs
   * fileDocumentMap[i] → extractionStatus[i] can track it.
   */
  const updateFileDocumentEntry = useCallback(
    (index: number, entry: FileDocumentEntry) => {
      setFileDocumentMap((prev) => {
        const next = [...prev];
        // Extend array if needed (fill gaps with placeholder entries)
        while (next.length <= index) {
          next.push({ filename: "", document_id: "" });
        }
        next[index] = entry;
        return next;
      });
    },
    [],
  );

  const fetchPendingJobs = useCallback(async (projectId: string): Promise<PendingJobsResponse> => {
    return api.get<PendingJobsResponse>(
      `/api/metadata-extraction/pending?project_id=${encodeURIComponent(projectId)}`,
    );
  }, []);

  const reset = useCallback(() => {
    stopAll();
    setIsPolling(false);
    setDocumentStatuses({});
    setFileDocumentMap([]);
    setQueueSize(0);
  }, [stopAll]);

  return {
    documentStatuses,
    fileDocumentMap,
    queueSize,
    isPolling,
    enqueue,
    retryDocument,
    updateFileDocumentEntry,
    startPolling,
    stopPolling,
    fetchPendingJobs,
    reset,
  };
}
